import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FacilityImpactPanel = ({ equipment, workOrders, onRefresh }) => {
  const [impactFilter, setImpactFilter] = useState('all');

  const calculateImpactLevel = (equipmentItem) => {
    // Calculate impact based on equipment status, location, and usage
    if (equipmentItem?.status === 'out-of-service') return 'high';
    if (equipmentItem?.status === 'maintenance') return 'medium';
    if (equipmentItem?.condition === 'poor') return 'medium';
    return 'low';
  };

  const getImpactData = () => {
    return equipment?.map((eq) => {
      const relatedWorkOrders = workOrders?.filter(
        wo => wo?.equipment_id === eq?.id && wo?.status !== 'completed'
      );
      
      const impactLevel = calculateImpactLevel(eq);
      const affectedMembers = Math.floor(Math.random() * 50) + 10; // Mock data
      const alternativeAvailable = equipment?.filter(
        alt => alt?.category === eq?.category && alt?.id !== eq?.id && alt?.status === 'operational'
      )?.length > 0;

      return {
        equipment: eq,
        impactLevel,
        affectedMembers,
        alternativeAvailable,
        workOrders: relatedWorkOrders,
        estimatedDowntime: relatedWorkOrders?.[0]?.scheduled_date ? 'Scheduled' : 'TBD'
      };
    })?.filter(item => {
      if (impactFilter === 'all') return true;
      return item?.impactLevel === impactFilter;
    })?.sort((a, b) => {
      const impactOrder = { high: 0, medium: 1, low: 2 };
      return impactOrder?.[a?.impactLevel] - impactOrder?.[b?.impactLevel];
    });
  };

  const impactData = getImpactData();

  const getImpactBadge = (level) => {
    const config = {
      high: { label: 'High Impact', color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle' },
      medium: { label: 'Medium Impact', color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertCircle' },
      low: { label: 'Low Impact', color: 'text-success', bg: 'bg-success/10', icon: 'Info' }
    };
    const cfg = config?.[level] || config?.low;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const impactStats = {
    high: impactData?.filter(item => item?.impactLevel === 'high')?.length || 0,
    medium: impactData?.filter(item => item?.impactLevel === 'medium')?.length || 0,
    low: impactData?.filter(item => item?.impactLevel === 'low')?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Impact Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-error/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertTriangle" size={24} className="text-error" />
            <span className="text-2xl font-bold text-error">{impactStats?.high}</span>
          </div>
          <p className="text-sm font-medium text-foreground">High Impact</p>
          <p className="text-xs text-muted-foreground mt-1">Requires immediate attention</p>
        </div>

        <div className="bg-card rounded-xl border border-warning/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertCircle" size={24} className="text-warning" />
            <span className="text-2xl font-bold text-warning">{impactStats?.medium}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Medium Impact</p>
          <p className="text-xs text-muted-foreground mt-1">Schedule maintenance soon</p>
        </div>

        <div className="bg-card rounded-xl border border-success/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Info" size={24} className="text-success" />
            <span className="text-2xl font-bold text-success">{impactStats?.low}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Low Impact</p>
          <p className="text-xs text-muted-foreground mt-1">Routine maintenance</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setImpactFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              impactFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            All Impact Levels
          </button>
          <button
            onClick={() => setImpactFilter('high')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              impactFilter === 'high' ? 'bg-error text-error-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            High Impact
          </button>
          <button
            onClick={() => setImpactFilter('medium')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              impactFilter === 'medium' ? 'bg-warning text-warning-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Medium Impact
          </button>
          <button
            onClick={() => setImpactFilter('low')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              impactFilter === 'low' ? 'bg-success text-success-foreground' : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            Low Impact
          </button>
        </div>
      </div>

      {/* Impact List */}
      <div className="space-y-4">
        {impactData?.map((item) => (
          <div
            key={item?.equipment?.id}
            className="bg-card rounded-xl border border-border p-4 md:p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{item?.equipment?.name}</h3>
                  {getImpactBadge(item?.impactLevel)}
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>
                    <Icon name="MapPin" size={14} className="inline mr-1" />
                    {item?.equipment?.location}
                  </p>
                  <p>
                    <Icon name="Package" size={14} className="inline mr-1" />
                    {item?.equipment?.category} - {item?.equipment?.type}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" iconName="Bell" iconPosition="left">
                  Notify Members
                </Button>
              </div>
            </div>

            {/* Impact Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Affected Members</p>
                <p className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Users" size={16} />
                  {item?.affectedMembers}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Alternative Equipment</p>
                <p className="text-lg font-semibold flex items-center gap-2">
                  {item?.alternativeAvailable ? (
                    <>
                      <Icon name="CheckCircle2" size={16} className="text-success" />
                      <span className="text-success">Available</span>
                    </>
                  ) : (
                    <>
                      <Icon name="XCircle" size={16} className="text-error" />
                      <span className="text-error">None</span>
                    </>
                  )}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Estimated Downtime</p>
                <p className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Clock" size={16} />
                  {item?.estimatedDowntime}
                </p>
              </div>
            </div>

            {/* Active Work Orders */}
            {item?.workOrders?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-foreground mb-2">Active Work Orders</p>
                <div className="space-y-2">
                  {item?.workOrders?.map((wo) => (
                    <div key={wo?.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{wo?.title}</p>
                        <p className="text-xs text-muted-foreground">Priority: {wo?.priority}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary capitalize">
                        {wo?.status?.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {impactData?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="CheckCircle2" size={48} className="mx-auto mb-4 text-success" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Facility Impact</h3>
          <p className="text-muted-foreground">All equipment is operational with minimal disruption</p>
        </div>
      )}
    </div>
  );
};

export default FacilityImpactPanel;